import type { CourseLevel, Lesson } from '../types/Skill.ts'

export const courseLevels: { value: CourseLevel; label: string }[] = [
    { value: 'beginner', label: 'Pre začiatočníkov' },
    { value: 'intermediate', label: 'Pre mierne pokročilých' },
    { value: 'advanced', label: 'Pre pokročilých' }
]

/** Only known YouTube hosts and video IDs may be embedded in lessons. */
export function getLessonVideoId(value?: string): string | undefined {
    if (!value) return undefined
    try {
        const url = new URL(value)
        if (url.protocol !== 'https:') return undefined
        const host = url.hostname.toLowerCase()
        const id =
            host === 'youtu.be'
                ? url.pathname.slice(1)
                : [
                        'youtube.com',
                        'www.youtube.com',
                        'm.youtube.com',
                        'youtube-nocookie.com',
                        'www.youtube-nocookie.com'
                    ].includes(host)
                  ? url.pathname === '/watch'
                      ? url.searchParams.get('v')
                      : url.pathname.match(/^\/(?:embed|shorts)\/([^/]+)$/)?.[1]
                  : undefined
        return id && /^[a-zA-Z0-9_-]{11}$/.test(id) ? id : undefined
    } catch {
        return undefined
    }
}

export function hasLessonContent(lesson: Lesson): boolean {
    return (
        !!lesson.content
            ?.replace(/<[^>]*>/g, '')
            .replace(/&nbsp;/g, '')
            .trim() || !!getLessonVideoId(lesson.videoUrl)
    )
}

/** Legacy lessons have no ID. Keep their identity when the editor first adds IDs. */
export function getLessonId(lesson: Lesson, index: number): string {
    if (lesson.id) return lesson.id
    let hash = 0
    for (const char of lesson.title || '') hash = (Math.imul(31, hash) + char.charCodeAt(0)) | 0
    return `legacy-${index}-${hash >>> 0}`
}

export function getCourseDuration(lessons: Lesson[]): number {
    return lessons.reduce(
        (total, lesson) =>
            total +
            (Number.isInteger(lesson.durationMinutes) && lesson.durationMinutes! > 0 ? lesson.durationMinutes! : 0),
        0
    )
}

export interface CourseProgress {
    completedLessonIds: string[]
    activeLessonId: string | null
}

/** Stored progress is untrusted and may refer to lessons removed since the last visit. */
export function readCourseProgress(value: unknown, lessonIds: string[]): CourseProgress {
    const progress = value && typeof value === 'object' ? (value as Partial<CourseProgress>) : {}
    const allowed = new Set(lessonIds)
    return {
        completedLessonIds: Array.isArray(progress.completedLessonIds)
            ? [...new Set(progress.completedLessonIds.filter((id) => typeof id === 'string' && allowed.has(id)))]
            : [],
        activeLessonId:
            typeof progress.activeLessonId === 'string' && allowed.has(progress.activeLessonId)
                ? progress.activeLessonId
                : null
    }
}
